import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authClient } from '../lib/auth-client';
import { AuthCard, Field, inputClass, primaryButtonClass, ErrorBanner } from '../components/AuthCard';
import { useAuthCapabilities } from '../hooks/useAuthCapabilities';
import { appendFlowFlag, resolvePostAuthPath } from '../lib/onboarding-flow';

// Signup creates the USER only. There is deliberately no "personal org"
// created on their behalf — the next step (/onboarding/create-org)
// requires them to explicitly name their organization. This keeps the
// org concept honest: every org exists because a human named it.
export default function Signup() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const caps = useAuthCapabilities();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const nextPath = resolvePostAuthPath(params.get('next'));
    const verificationTarget = appendFlowFlag(nextPath, 'verified');
    const callbackURL = typeof window !== 'undefined' ? `${window.location.origin}${verificationTarget}` : undefined;
    const { error: err } = await authClient.signUp.email({ email, password, name, callbackURL });
    setSubmitting(false);
    if (err) {
      setError(err.message || 'Could not create account.');
      return;
    }
    if (caps?.emailVerificationRequired) {
      navigate(`/verify-email?email=${encodeURIComponent(email)}&next=${encodeURIComponent(verificationTarget)}`, { replace: true });
      return;
    }
    navigate(nextPath, { replace: true });
  }

  return (
    <AuthCard
      title="Create account"
      subtitle="Create your account first. You’ll name the organization workspace in the next step."
      footer={
        <>
          Already have an account?{' '}
          <Link to={`/login${params.get('next') ? `?next=${params.get('next')!}` : ''}`} className="font-semibold text-amber-700 hover:text-amber-800 hover:underline">
            Sign in
          </Link>
        </>
      }
    >
      <ErrorBanner message={error} />
      <form onSubmit={onSubmit} className="space-y-4">
        <Field label="Your name">
          <input
            type="text"
            required
            autoComplete="name"
            autoFocus
            value={name}
            onChange={e => setName(e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field label="Work email">
          <input
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field label="Password">
          <input
            type="password"
            required
            autoComplete="new-password"
            minLength={10}
            value={password}
            onChange={e => setPassword(e.target.value)}
            className={inputClass}
          />
        </Field>
        <p className="rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-2 text-xs leading-5 text-slate-600">
          At least 10 characters. You’ll name your organization next.
        </p>
        <button type="submit" disabled={submitting} className={primaryButtonClass}>
          {submitting ? 'Creating account…' : 'Continue'}
        </button>
      </form>
    </AuthCard>
  );
}
